/**
 * テナント管理 API hooks
 * Python Backend (PostgreSQL) 用
 */
import { useCallback, useState } from "react";
import { restClient } from "../lib/rest-client";

// =====================================
// Types
// =====================================

export interface Tenant {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  role: "owner" | "admin" | "member";
}

export interface CreateTenantInput {
  name: string;
  description?: string;
}

// =====================================
// useCreateTenant - テナント作成
// =====================================

export interface UseCreateTenantResult {
  createTenant: (input: CreateTenantInput) => Promise<Tenant>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * テナントを作成するhook
 * @return {UseCreateTenantResult} 作成関数、ローディング状態、エラー
 */
export function useCreateTenant(): UseCreateTenantResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createTenant = useCallback(
    async (input: CreateTenantInput): Promise<Tenant> => {
      setLoading(true);
      setError(undefined);

      try {
        const tenant = await restClient.post<Tenant>("/tenants", input);
        return tenant;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    createTenant,
    loading,
    error,
  };
}
